import palette from './ColorPalette';
import { isNil, get, omit, isNumber, isEmpty, flatMap } from 'lodash';
import { EMPTY_STRING_TOKEN } from '../Constants';
import { scaleOrdinal } from 'd3';

export function scaleOrdinalWithDomainCallback(onDomainAdd) {
  const scale = scaleOrdinal().range(palette);
  const known = new Set();

  const wrapped = key => {
    if (!known.has(key)) {
      known.add(key);
      onDomainAdd(key, scale);
    }
    return scale(key);
  };
  Object.setPrototypeOf(wrapped, scale);
  return wrapped;
}

class ColorManager {
  constructor() {
    this.db = {};
    this.custom = {};
    this.colorGroup = {};
    this.sugarCoreColors = {};
  }

  mergeWithSugarColors(scale, sugarCoreColor) {
    const domain = scale.domain();
    const range = scale.range();
    if (sugarCoreColor) {
      // every new color added with group(colorName) is not in the domain yet
      const index = domain.length;
      range[index] = sugarCoreColor;
    }

    scale.range(range);
  }

  addGroup(name, force = false, colorSet = []) {
    if (!this.db[name] || force) {
      this.db[name] = isEmpty(colorSet)
        ? scaleOrdinalWithDomainCallback((key, scale) =>
            this.mergeWithSugarColors(scale, this.sugarCoreColors[key]),
          )
        : scaleOrdinal(flatMap(colorSet, obj => Object.values(obj))).domain(
            flatMap(colorSet, obj => Object.keys(obj)),
          );
    }
    return this.db[name];
  }

  getGroup(name) {
    return this.db[name] ? this.db[name] : this.addGroup(name);
  }

  getCustomGroup(name) {
    return this.custom[name];
  }

  getColor(groupName, colorName, theme) {
    if (isNil(colorName)) {
      return theme?.colors?.ContentBackground;
    }
    if (colorName === '') {
      colorName = EMPTY_STRING_TOKEN;
    }
    const group = this.getGroup(groupName);
    const defaultColorForColorGroup = this.getColorForColorGroup(
      groupName,
      colorName,
    );
    const defaultColor = group(colorName);
    const custom = get(this.custom, groupName);

    const color =
      defaultColorForColorGroup || get(custom, colorName, defaultColor);

    return get(custom?.colorGroup, colorName, color);
  }

  setCustomColors(groupName, colors) {
    this.custom[groupName] = colors;
  }
  clearCustomColors(groupName) {
    this.custom[groupName] = {};
  }

  setSugarCoreColors(colors) {
    this.sugarCoreColors = colors;
  }

  reset(groupName) {
    return this.addGroup(groupName, true);
  }

  updateGroupKey(groupName, newKey) {
    if (this.db[groupName]) {
      this.db[newKey] = this.db[groupName];
      delete this.db[groupName];
    }
  }
  /**
   * Sets the color value for multiple fields within a color group based on a specified color name.
  
   *
   * @param {string} groupName - Viz id.
   * @param {string} colorName - Color group id.
   * @param {string[]} [fields=[]] - An array of field names.
   * @modifies {this.colorGroup} - { groupName: { colorName: colorValue, field1: colorValue, field2: colorValue, ... } }
   */
  setColorsForColorGroup(groupName, colorName, fields = []) {
    const mainGroupColor = this.colorGroup?.[groupName]?.[colorName];
    if (mainGroupColor) {
      fields.forEach(fieldName => {
        this.colorGroup[groupName][fieldName] = mainGroupColor;
      });
    }
  }
  /**
   * @param groupName - viz id
   * @param colorName - color group id
   * @param idx - index of the color in the default color palette
   * @param fields - array of field names
   */
  getColorForColorGroup(groupName, colorName, idx, fields = []) {
    if (isNumber(idx) && idx >= 0) {
      if (!this.colorGroup?.[groupName]?.[colorName]) {
        this.colorGroup[groupName] = {
          ...this.colorGroup[groupName],
          [colorName]: palette[idx],
        };
      }
      this.setColorsForColorGroup(groupName, colorName, fields);
    }
    const defaultColor = this.colorGroup?.[groupName]?.[colorName];
    const custom = get(this.custom, groupName);

    return get(custom?.colorGroup, colorName, defaultColor);
  }

  removeColorsFromGroup(groupName, colorNames) {
    this.colorGroup[groupName] = omit(this.colorGroup[groupName], colorNames);
  }
}

const colorManager = new ColorManager();

export default colorManager;
